import { useState, useEffect, useCallback } from 'react';
import {
	Box,
	Typography,
	Switch,
	IconButton,
	Menu,
	MenuItem,
	ListItemIcon,
	ListItemText,
	Button,
	useMediaQuery,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import { authedApiFetch } from '../../../../../utils/Api';
import CategoriesDrawer from './CategoriesDrawer';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import { StyledDataGridContainer } from '../../common/StyledDataGridContainer';

const Categories = ({ showNotification, tenantId }) => {
	const theme = useTheme();
	const [isLoading, setIsLoading] = useState(false);
	const [categories, setCategories] = useState([]);
	const [configListId, setConfigListId] = useState(null);
	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedRowForMenu, setSelectedRowForMenu] = useState(null);

	// Confirmation dialog state
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [categoryToDelete, setCategoryToDelete] = useState(null);

	// Category drawer state (for both add and edit)
	const [addCategoryDrawerOpen, setAddCategoryDrawerOpen] = useState(false);
	const [editCategoryDrawerOpen, setEditCategoryDrawerOpen] = useState(false);
	const [editCategoryData, setEditCategoryData] = useState({
		displayName: '',
		description: '',
		active: true,
	});

	const [newCategoryData, setNewCategoryData] = useState({
		displayName: '',
		description: '',
		active: true,
	});

	const valueFromDisplayName = (displayName) =>
		displayName.toLowerCase().replace(/\s+/g, '_');

	const fetchCategories = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/config_lists?list_type=categories`,
				method: 'GET',
			});

			if (response?.[0]?.config_list_items) {
				setConfigListId(response[0].id);

				// order by last modified / created
				const sortedItems = response[0].config_list_items.sort(
					(a, b) => new Date(b.updated_at) - new Date(a.updated_at)
				);

				const gridData = sortedItems.map((item) => ({
					id: item.id,
					displayName: item.display_name || '',
					description: item.description || '',
					active: item.active,
				}));

				setCategories(gridData);
			} else {
				showNotification('Failed to load categories', 'error');
			}
		} catch (error) {
			showNotification('Error loading categories', 'error');
		} finally {
			setIsLoading(false);
		}
	}, [tenantId]);

	// Menu handlers
	const handleOpenMenu = (event, row) => {
		setAnchorEl(event.currentTarget);
		setSelectedRowForMenu(row);
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
		setSelectedRowForMenu(null);
	};

	// Handle menu item click
	const handleMenuItemClick = (action) => {
		setAnchorEl(null);
		if (action === 'delete') {
			setCategoryToDelete(selectedRowForMenu);
			setDeleteDialogOpen(true);
		} else if (action === 'edit') {
			// Initialize edit form data with the selected category's values
			setEditCategoryData({
				displayName: selectedRowForMenu.displayName || '',
				description: selectedRowForMenu.description || '',
				active:
					selectedRowForMenu.active !== undefined
						? selectedRowForMenu.active
						: true,
				id: selectedRowForMenu.id, // config_list_item ID needed for api call
			});
			setEditCategoryDrawerOpen(true);
		}
	};

	// Delete category
	const deleteCategory = async (category) => {
		const config_list_id = configListId;
		try {
			await authedApiFetch({
				endpoint: `/tenants/${tenantId}/config_lists/${config_list_id}/config_list_items/${category.id}`,
				method: 'DELETE',
			});
			fetchCategories(); // Refresh the list
			showNotification('Category deleted successfully');
		} catch (error) {
			showNotification('Error deleting category', 'error');
		}
	};

	const handleDeleteConfirm = () => {
		if (categoryToDelete) {
			deleteCategory(categoryToDelete);
		}
		setDeleteDialogOpen(false);
		setCategoryToDelete(null);
	};

	const handleDeleteCancel = () => {
		setDeleteDialogOpen(false);
		setCategoryToDelete(null);
	};

	// Add Category handlers
	const handleAddCategoryClick = () => {
		setNewCategoryData({
			displayName: '',
			description: '',
			active: true,
		});
		setAddCategoryDrawerOpen(true);
	};

	const handleAddCategoryClose = () => {
		setAddCategoryDrawerOpen(false);
	};

	const handleNewCategoryChange = (e) => {
		const { name, value } = e.target;
		setNewCategoryData((prev) => ({ ...prev, [name]: value }));
	};

	// Edit Category handlers
	const handleEditCategoryClose = () => {
		setEditCategoryDrawerOpen(false);
	};

	const handleEditCategoryChange = (e) => {
		const { name, value } = e.target;
		setEditCategoryData((prev) => ({ ...prev, [name]: value }));
	};

	const handleEditCategorySubmit = async () => {
		try {
			const response = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/config_lists/${configListId}/config_list_items/${editCategoryData.id}`,
				method: 'PUT',
				payload: {
					config_list_item: {
						display_name: editCategoryData.displayName,
						active: editCategoryData.active,
						description: editCategoryData.description,
						value: valueFromDisplayName(editCategoryData.displayName),
					},
				},
			});

			if (response) {
				fetchCategories(); // Refresh the list
				showNotification('Category updated successfully');
				setEditCategoryDrawerOpen(false);
			}
		} catch (error) {
			showNotification('Error updating category', 'error');
		}
	};

	const handleAddCategorySubmit = async () => {
		try {
			const response = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/config_lists/${configListId}/config_list_items`,
				method: 'POST',
				payload: {
					config_list_item: {
						display_name: newCategoryData.displayName,
						active: newCategoryData.active,
						description: newCategoryData.description,
						value: valueFromDisplayName(newCategoryData.displayName),
					},
				},
			});

			if (response) {
				fetchCategories(); // Refresh the list
				showNotification('Category added successfully');
				setAddCategoryDrawerOpen(false);
			}
		} catch (error) {
			showNotification('Error adding category', 'error');
		}
	};

	const updateCategory = async (category) => {
		try {
			const config_list_id = configListId;
			await authedApiFetch({
				endpoint: `/tenants/${tenantId}/config_lists/${config_list_id}/config_list_items/${category.id}`,
				method: 'PUT',
				payload: {
					config_list_item: {
						display_name: category.displayName,
						description: category.description,
						active: category.active,
						value: valueFromDisplayName(category.displayName),
					},
				},
			});
			fetchCategories(); // Refresh the list
			showNotification('Category updated successfully');
		} catch (error) {
			showNotification('Error updating category', 'error');
		}
	};

	const handleActiveToggle = (event, row) => {
		const updatedRow = { ...row, active: event.target.checked };
		updateCategory(updatedRow);
	};

	const handleRowUpdate = (newRow, oldRow) => {
		// Check if any values have changed
		if (
			newRow.displayName !== oldRow.displayName ||
			newRow.description !== oldRow.description ||
			newRow.active !== oldRow.active
		) {
			updateCategory(newRow);
		}
		return newRow;
	};

	const columns = [
		{
			field: 'actions',
			headerName: '',
			width: 70,
			sortable: false,
			renderCell: (params) => (
				<Box>
					<IconButton
						size='small'
						onClick={(e) => handleOpenMenu(e, params.row)}
					>
						<MoreVertIcon />
					</IconButton>
					<Menu
						anchorEl={anchorEl}
						open={Boolean(anchorEl)}
						onClose={handleCloseMenu}
						PaperProps={{
							elevation: 1,
							sx: { minWidth: 180 },
						}}
					>
						<MenuItem onClick={() => handleMenuItemClick('edit')}>
							<ListItemIcon>
								<EditIcon fontSize='small' />
							</ListItemIcon>
							<ListItemText>Edit Category</ListItemText>
						</MenuItem>
						<MenuItem onClick={() => handleMenuItemClick('delete')}>
							<ListItemIcon>
								<DeleteIcon fontSize='small' color='error' />
							</ListItemIcon>
							<ListItemText>Delete Category</ListItemText>
						</MenuItem>
					</Menu>
				</Box>
			),
		},
		{ field: 'displayName', headerName: 'Name', flex: 1, editable: true },
		{
			field: 'description',
			headerName: 'Description',
			flex: 1.5,
			editable: true,
		},
		{
			field: 'active',
			headerName: 'Active',
			width: 100,
			editable: true,
			renderCell: (params) => (
				<Switch
					checked={params.row.active}
					onChange={(e) => handleActiveToggle(e, params.row)}
					color='primary'
				/>
			),
		},
	];

	useEffect(() => {
		fetchCategories();
	}, [fetchCategories]);

	return (
		<Box sx={{ p: 3 }}>
			<Typography variant='h6'>Categories</Typography>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					mb: 2,
				}}
			>
				<Typography variant='body2' color='text.secondary'>
					Define the categories used to classify deals in your investment
					process.
				</Typography>
				<Button
					variant='contained'
					color='primary'
					size='small'
					onClick={handleAddCategoryClick}
				>
					Add Category
				</Button>
			</Box>

			<StyledDataGridContainer>
				<DataGrid
					rows={categories}
					columns={columns}
					loading={isLoading}
					processRowUpdate={handleRowUpdate}
					experimentalFeatures={{ newEditingApi: true }}
					disableSelectionOnClick
				/>
			</StyledDataGridContainer>

			<ConfirmationDialog
				open={deleteDialogOpen}
				handleConfirm={handleDeleteConfirm}
				handleCancel={handleDeleteCancel}
				title='Delete Category'
				message='Are you sure you want to delete this category?'
				confirmActionButtonLabel='Delete'
				confirmActionButtonColor='error'
				cancelActionButtonColor='primary'
			/>

			{/* Add Category Drawer */}
			<CategoriesDrawer
				open={addCategoryDrawerOpen}
				onClose={handleAddCategoryClose}
				onSubmit={handleAddCategorySubmit}
				formData={newCategoryData}
				onChange={handleNewCategoryChange}
				isMobile={useMediaQuery(theme.breakpoints.down('sm'))}
			/>

			{/* Edit Category Drawer */}
			<CategoriesDrawer
				open={editCategoryDrawerOpen}
				onClose={handleEditCategoryClose}
				onSubmit={handleEditCategorySubmit}
				formData={editCategoryData}
				onChange={handleEditCategoryChange}
				isMobile={useMediaQuery(theme.breakpoints.down('sm'))}
				editMode={true}
			/>
		</Box>
	);
};

export default Categories;
